import type { PageFormat } from "./format";
import { createStoredPreference } from "./storedPreference";

/**
 * Which editor sits behind `PageEditor`:
 * - `astromark`: `@orbitalwatch/astromark-editor` (WYSIWYG / split / source, AstroMark preview);
 * - `legacy`: `react-markdown-editor-lite`, the editor Ark has always used, with `renderPage` as its preview;
 * - `auto` (default): `astromark` for AstroMark pages, `legacy` for markdown-it pages. The AstroMark editor previews (and,
 *   in WYSIWYG, re-serialises) a body as AstroMark, which would rewrite a markdown-it page; so markdown-it pages keep the
 *   legacy editor until they are migrated.
 *
 * The user's own choice (the account menu's "Editor" group) is kept per browser in localStorage (`ark.pageEditor`) and
 * sits between the dev `?editor=` override and the page's format.
 */
export type PageEditorKind = "auto" | "astromark" | "legacy";

/** Module default for `editor`: flip to `legacy` to take the AstroMark editor out everywhere without touching screens. */
export const DEFAULT_PAGE_EDITOR: PageEditorKind = "auto";

/** The localStorage key of the user's editor choice. */
export const EDITOR_PREFERENCE_STORAGE_KEY = "ark.pageEditor";
export const EDITOR_PREFERENCE_EVENT = "ark:page-editor";

/** A stored value as an editor kind; anything unknown is `auto`. */
export function parsePageEditorKind(value: unknown): PageEditorKind {
    return value === "astromark" || value === "legacy" ? value : "auto";
}

const preference = createStoredPreference<PageEditorKind>(EDITOR_PREFERENCE_STORAGE_KEY, EDITOR_PREFERENCE_EVENT, parsePageEditorKind, "auto");

/** The user's editor choice (`auto` when unset); `setEditorPreference` stores it; `useEditorPreference` follows it. */
export const getEditorPreference = preference.get;
export const setEditorPreference = preference.set;
export const subscribeEditorPreference = preference.subscribe;
export const useEditorPreference = preference.use;

function editorFromSearch(search: string | undefined): "astromark" | "legacy" | undefined {
    if (!search) return undefined;
    try {
        const value = new URLSearchParams(search).get("editor");
        return value === "astromark" || value === "legacy" ? value : undefined;
    } catch {
        return undefined;
    }
}

/**
 * The editor for a page: an explicit `astromark`/`legacy` wins, then the dev `?editor=` override, then the user's stored
 * choice (unless `auto`), then by format.
 */
export function resolvePageEditor(
    format: PageFormat,
    kind: PageEditorKind = DEFAULT_PAGE_EDITOR,
    search: string | undefined = typeof window !== "undefined" ? window.location.search : undefined,
    allowOverride: boolean = import.meta.env.DEV,
    preference: PageEditorKind = getEditorPreference()
): "astromark" | "legacy" {
    if (kind !== "auto") return kind;
    const override = allowOverride ? editorFromSearch(search) : undefined;
    if (override) return override;
    if (preference !== "auto") return preference;
    return format === "astromark" ? "astromark" : "legacy";
}
